import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, BadgeCheck, Search, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api, { vimg } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { ProductDetail } from "@/components/landing/ProductDetail";

const VIAL =
  "https://images.unsplash.com/photo-1770320742319-6aa889b3130b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxsYWJvcmF0b3J5JTIwdmlhbCUyMGRhcmslMjBsaWdodGluZ3xlbnwwfHx8fDE3OTA0OTMwMzN8MA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const spanFor = (i) => (i === 0 ? "md:col-span-2 md:row-span-2" : i === 1 ? "md:row-span-2" : "");

export const Products = () => {
  const [products, setProducts] = useState(null);
  const [verified, setVerified] = useState(new Set());
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const { add } = useCart();
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/reports").then((r) => setVerified(new Set(r.data.map((rep) => rep.product_code)))).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set((products || []).map((p) => p.category))).sort()],
    [products]
  );

  const filtered = useMemo(() => {
    if (!products) return null;
    const q = query.trim().toLowerCase();
    const list = products.filter(
      (p) =>
        (category === "All" || p.category === category) &&
        (!q ||
          p.name.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          String(p.size).toLowerCase().includes(q))
    );
    if (sort === "price-asc") return [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") return [...list].sort((a, b) => b.price - a.price);
    if (sort === "name") return [...list].sort((a, b) => a.name.localeCompare(b.name) || String(a.size).localeCompare(String(b.size)));
    return list;
  }, [products, category, query, sort]);

  const addToCart = (p) => {
    add(p);
    toast.success(`${p.name} ${p.size} added to cart`);
  };

  return (
    <section id="products" data-testid="products-section" className="relative py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div>
            <p className="font-mono-clinical text-xs tracking-[0.3em] uppercase text-[#6D28D9] mb-4">01 — Catalog</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight uppercase">Research Compounds</h2>
          </div>
          <p className="text-[#71717A] text-sm md:text-base max-w-sm">
            Every batch is HPLC and mass-spec verified. Certificates of analysis ship with each order.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6"
          data-testid="catalog-toolbar"
        >
          <div className="flex flex-wrap gap-2" data-testid="catalog-category-pills">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`catalog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-4 py-2 font-mono-clinical text-[11px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
                  category === c
                    ? "bg-[#6D28D9] text-white border-[#6D28D9]"
                    : "border-[#E4E4E7] text-[#71717A] hover:border-[#6D28D9]/50 hover:text-[#09090B]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-1 gap-3 lg:justify-end">
            <div className="relative flex-1 lg:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" strokeWidth={1.5} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search compounds…"
                data-testid="catalog-search-input"
                className="w-full bg-transparent border border-[#E4E4E7] pl-9 pr-3 py-2 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9]/60 transition-colors duration-300"
              />
            </div>
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="catalog-sort-select"
                className="appearance-none bg-transparent border border-[#E4E4E7] pl-3 pr-9 py-2 font-mono-clinical text-[11px] tracking-[0.15em] uppercase text-[#52525B] focus:outline-none focus:border-[#6D28D9]/60 transition-colors duration-300 cursor-pointer"
              >
                <option value="featured" className="bg-black">Featured</option>
                <option value="price-asc" className="bg-black">Price: Low to High</option>
                <option value="price-desc" className="bg-black">Price: High to Low</option>
                <option value="name" className="bg-black">Name: A to Z</option>
              </select>
              <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A1A1AA] pointer-events-none" strokeWidth={1.5} />
            </div>
          </div>
        </motion.div>

        {filtered && (
          <p data-testid="catalog-result-count" className="font-mono-clinical text-[11px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-6">
            {filtered.length} compound{filtered.length === 1 ? "" : "s"}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 md:auto-rows-[260px] gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
          {products === null
            ? [0, 1, 2, 3].map((i) => (
                <div key={i} className={`bg-[#FFFFFF] animate-pulse min-h-[260px] ${spanFor(i)}`} />
              ))
            : filtered.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 48 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.9, delay: (i % 4) * 0.08, ease: EASE }}
                  onClick={() => setSelected(p)}
                  data-testid={`product-card-${p.code}`}
                  className={`group relative bg-[#FFFFFF] overflow-hidden flex flex-col cursor-pointer ${spanFor(i)}`}
                >
                  <div className="relative flex-1 min-h-[160px] overflow-hidden">
                    <img
                      src={vimg(p.image_url || VIAL)}
                      alt={`${p.name} research vial`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    {p.structure_url && (
                      <img
                        src={vimg(p.structure_url)}
                        alt={`${p.name} chemical structure`}
                        data-testid={`product-structure-${p.code}`}
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-[#FFFFFF]/20 to-transparent" />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{ background: "radial-gradient(400px circle at 50% 35%, rgba(109,40,217,0.12), transparent 65%)" }}
                    />
                    {!p.in_stock && (
                      <span className="absolute top-4 right-4 font-mono-clinical text-[10px] tracking-[0.2em] uppercase px-3 py-1 border border-[#D4D4D8] text-[#71717A] bg-black/60">
                        On Request
                      </span>
                    )}
                  </div>
                  <div className="relative p-6 md:p-7">
                    <div className="flex items-center justify-between font-mono-clinical text-[11px] tracking-[0.2em] uppercase mb-3">
                      <span className="text-[#6D28D9] flex items-center gap-1.5">
                        {p.code}
                        {verified.has(p.code) && (
                          <span data-testid={`janoshik-badge-${p.code}`} title="Janoshik verified" className="inline-flex items-center gap-1 text-[#71717A] normal-case tracking-normal">
                            <BadgeCheck className="w-3.5 h-3.5 text-[#6D28D9]" strokeWidth={1.5} />
                          </span>
                        )}
                      </span>
                      <span className="text-[#A1A1AA]">{p.purity}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight uppercase text-[#09090B]">
                      {p.name} <span className="text-[#A1A1AA] text-lg md:text-xl">{p.size}</span>
                    </h3>
                    <p className="text-sm text-[#71717A] mt-2 leading-relaxed line-clamp-2">{p.description}</p>
                    <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#E4E4E7]">
                      <span className="font-mono-clinical text-sm text-[#09090B]">${Number(p.price).toFixed(0)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); addToCart(p); }}
                        data-testid={`product-add-${p.code}`}
                        className="flex items-center gap-1.5 px-4 py-2 border border-[#6D28D9]/50 text-[#6D28D9] font-mono-clinical text-[11px] tracking-[0.15em] uppercase hover:bg-[#6D28D9] hover:text-white transition-colors duration-300"
                      >
                        Add <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
          {filtered !== null && filtered.length === 0 && (
            <div
              data-testid="catalog-empty-state"
              className="sm:col-span-2 md:col-span-4 bg-[#FFFFFF] min-h-[260px] flex items-center justify-center"
            >
              <p className="font-mono-clinical text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                No compounds match your search
              </p>
            </div>
          )}
        </div>
      </div>
      <ProductDetail
        product={selected}
        verified={selected ? verified.has(selected.code) : false}
        onClose={() => setSelected(null)}
        onAdd={(p) => { addToCart(p); setSelected(null); }}
      />
    </section>
  );
};
